import { useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { cpf as cpfValidator, cnpj } from 'cpf-cnpj-validator';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ImageLogo } from '~/assets/images';
import { Button, Input, InputMask } from '~/components';
import { useAuth } from '~/hooks/auth';
import getValidationErrors from '~/utils/getValidationErrors';
import { regex_cpf, regex_remove_all_non_digits } from '~/utils/regex';

import {
  Container,
  Wrapper,
  Title,
  Subtitle,
  OptionsContainer,
  SignUpNow,
  Slogan,
} from './styles';

interface SignInFormData {
  cpf: string;
  password: string;
}

const SignIn = () => {
  const { t } = useTranslation(['Yup', 'Toast', 'Professional:SignIn']);
  const { push } = useHistory();
  const { signInProfessional } = useAuth();
  const formRef = useRef<FormHandles>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const { toggleToRememberMe } = useAuth();

  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (data: SignInFormData) => {
      try {
        setLoading(true);
        const cpf_cnpj = data.cpf;

        if (String(cpf_cnpj).length !== 11 && String(cpf_cnpj).length !== 14) {
          toast.error('Formato inválido');
          setLoading(false);
          return;
        }

        if (String(cpf_cnpj).length === 11 && !cpfValidator.isValid(cpf_cnpj)) {
          toast.error('CPF inválido');
          setLoading(false);
          return;
        }

        if (String(cpf_cnpj).length === 14 && !cnpj.isValid(cpf_cnpj)) {
          toast.error('CNPJ inválido');
          setLoading(false);
          return;
        }

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          // cpf: Yup.string().required(t('Yup:cpf_required')).matches(regex_cpf, {
          //   excludeEmptyString: true,
          // }),

          password: Yup.string().required(t('Yup:password_required')),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { cpf, password } = data;

        const cpf_formatted = cpf.replace(regex_remove_all_non_digits, '');

        const response = await signInProfessional({
          cpf: String(cpf_cnpj),
          password,
        });

        if (response) {
          push('/professional/students');
          toast.success(t('Toast:success'));
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error(t('Toast:error'));
      }

      setLoading(false);
    },
    [push, signInProfessional, t],
  );

  return (
    <Container>
      <Wrapper>
        <ImageLogo />

        <Title>Acesse sua conta</Title>
        <Subtitle>Treinador</Subtitle>

        <Form ref={formRef} onSubmit={handleSubmit}>
          <Input
            name="cpf"
            placeholder={t('Professional:SignIn.cpf_cnpj_placeholder')}
            type="number"
            maxLength={14}
          />

          <Input
            name="password"
            placeholder={t('Professional:SignIn.password_placeholder')}
            showIconPassword
          />
        </Form>

        <OptionsContainer>
          <label htmlFor="rememberMe">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              ref={checkboxRef}
              onClick={toggleToRememberMe}
            />
            <span style={{ marginLeft: 4 }}>
              {t('Professional:SignIn.remember_me')}
            </span>
          </label>

          <span>
            <Link to="forget-password">
              {t('Professional:SignIn.forget_password')}
            </Link>
          </span>
        </OptionsContainer>

        <Button
          onClick={() => formRef.current?.submitForm()}
          style={{ maxWidth: 387 }}
          loading={loading}
        >
          {t('Professional:SignIn.button_login')}
        </Button>

        <SignUpNow>
          {/* {t('Professional:SignIn.sign_up_account')}{' '}
          <Link to="/professional/sign-up">
            {t('Professional:SignIn.sign_up_now')}
          </Link> */}
        </SignUpNow>

        <Slogan>
          <p>{t('Professional:SignIn.slogan_your_business')}</p>
          <p>{t('Professional:SignIn.slogan_your_hand')}</p>
        </Slogan>
      </Wrapper>
    </Container>
  );
};

export default SignIn;
